/**
 * Seconds-based adapter that mirrors the shared `window.player` surface (see videojsPlayerAdapter.js)
 * and maps core transport to the Grass Valley SCS Player API (frame-based).
 *
 * No MIDI / audio-scrub wiring — core transport only.
 */

import {
    playerState,
    updateTime,
    updatePlayingStatus,
    updatePlayerSpeed,
} from "@app/store/playerStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";

/**
 * @param {object|null} realPlayer
 * @returns {number}
 */
function resolveFps(realPlayer) {
    const sampleRate = realPlayer?.sampleRate;
    if (
        Array.isArray(sampleRate) &&
        sampleRate.length >= 2 &&
        sampleRate[1]
    ) {
        const fps = sampleRate[0] / sampleRate[1];
        if (Number.isFinite(fps) && fps > 0) return fps;
    }
    const fallback = Number(projectState.frameRate) || 24;
    return fallback > 0 ? fallback : 24;
}

/**
 * @param {number} frames
 * @param {object|null} realPlayer
 */
function framesToSeconds(frames, realPlayer) {
    const fps = resolveFps(realPlayer);
    return (Number(frames) || 0) / fps;
}

/**
 * @param {number} seconds
 * @param {object|null} realPlayer
 */
function secondsToFrames(seconds, realPlayer) {
    const fps = resolveFps(realPlayer);
    return Math.round((Number(seconds) || 0) * fps);
}

/**
 * @param {{ embedUrl?: string, materialId?: string, playerBaseUrl?: string }} [options]
 */
export function createGvPlayerAdapter(options = {}) {
    let currentTime = 0;
    let duration = 0;
    let playing = false;
    let playbackRate = 1;
    let volume = 1;
    let src = options.embedUrl || options.playerBaseUrl || "";
    let poster = "";
    let materialId = options.materialId || "";
    let playerBaseUrl = options.playerBaseUrl || "";
    /** @type {object|null} */
    let realPlayer = null;

    let onPosition = null;
    let onPlayState = null;
    let onLoadState = null;

    function detachListeners() {
        if (!realPlayer) return;
        if (onPosition) {
            realPlayer.removeEventListener("position", onPosition);
            onPosition = null;
        }
        if (onPlayState) {
            realPlayer.removeEventListener("playState", onPlayState);
            onPlayState = null;
        }
        if (onLoadState) {
            realPlayer.removeEventListener("loadState", onLoadState);
            onLoadState = null;
        }
    }

    function attachListeners(instance) {
        onPosition = (data) => {
            if (!data) return;
            currentTime = framesToSeconds(data.frame, instance);
            if (data.duration != null) {
                duration = framesToSeconds(data.duration, instance);
                playerState.duration = duration;
            }
            updateTime(currentTime);
        };

        onPlayState = (data) => {
            if (!data) return;
            playing = !!data.playing;
            updatePlayingStatus(playing);
            if (data.playSpeed != null && Number.isFinite(data.playSpeed)) {
                playbackRate = data.playSpeed;
                updatePlayerSpeed(playbackRate);
            }
        };

        onLoadState = (state) => {
            if (state === "error") {
                console.error("[GvPlayerAdapter] SCS loadState error");
            } else if (state === "loaded" && realPlayer) {
                const d = realPlayer.duration;
                if (d != null) {
                    duration = framesToSeconds(d, realPlayer);
                    playerState.duration = duration;
                }
            }
        };

        instance.addEventListener("position", onPosition);
        instance.addEventListener("playState", onPlayState);
        instance.addEventListener("loadState", onLoadState);
    }

    const adapter = {
        play: async () => {
            if (realPlayer?.play) {
                await realPlayer.play(playbackRate);
                return;
            }
            playing = true;
            updatePlayingStatus(true);
        },

        pause: async () => {
            if (realPlayer?.stop) {
                await realPlayer.stop();
                return;
            }
            playing = false;
            updatePlayingStatus(false);
        },

        get currentTime() {
            if (realPlayer?.currentFrame != null) {
                return framesToSeconds(realPlayer.currentFrame, realPlayer);
            }
            return currentTime;
        },
        set currentTime(value) {
            const seconds = Number(value) || 0;
            currentTime = seconds;
            updateTime(currentTime);
            if (realPlayer?.seek) {
                const frame = secondsToFrames(seconds, realPlayer);
                realPlayer.seek(frame).catch((err) => {
                    console.warn("[GvPlayerAdapter] seek failed:", err);
                });
            }
        },

        get duration() {
            if (realPlayer?.duration != null) {
                return framesToSeconds(realPlayer.duration, realPlayer);
            }
            return duration;
        },
        set duration(value) {
            duration = Number(value) || 0;
        },

        get playbackRate() {
            return playbackRate;
        },
        set playbackRate(value) {
            playbackRate = Number(value) || 1;
            updatePlayerSpeed(playbackRate);
            if (realPlayer?.play && playing) {
                realPlayer.play(playbackRate).catch((err) => {
                    console.warn("[GvPlayerAdapter] play rate change failed:", err);
                });
            }
        },

        get volume() {
            return volume;
        },
        set volume(value) {
            volume = Number(value);
            if (realPlayer?.setVolume) {
                realPlayer.setVolume(Math.round(Math.min(1, Math.max(0, volume)) * 100));
            }
        },

        get src() {
            return src;
        },
        set src(value) {
            if (typeof value === "string") {
                src = value;
            } else if (value && typeof value === "object" && value.src) {
                src = value.src;
            } else {
                src = "";
            }
        },

        get poster() {
            return poster;
        },
        set poster(value) {
            poster = value || "";
        },

        get materialId() {
            return materialId;
        },

        get playerBaseUrl() {
            return playerBaseUrl;
        },

        state: {
            get playing() {
                return playing;
            },
            get ended() {
                return false;
            },
        },

        /**
         * Update FLX metadata when importMedia is called again in framelight mode.
         * Prefer loadMaterial / recreate when base URL changes (handled by GvMediaPlayer).
         */
        setEmbedUrl(embedUrl, nextMaterialId = "", nextPlayerBaseUrl = "") {
            src = embedUrl || nextPlayerBaseUrl || src;
            if (nextMaterialId) materialId = nextMaterialId;
            if (nextPlayerBaseUrl) playerBaseUrl = nextPlayerBaseUrl;
        },

        /**
         * @param {object|null} instance SCS Player instance
         */
        attachRealPlayer(instance) {
            detachListeners();
            if (realPlayer && realPlayer !== instance) {
                try {
                    realPlayer.unload?.();
                    // SCS Player.js has no public dispose; clean MessageManager if present
                    if (typeof realPlayer.dispose === "function") {
                        realPlayer.dispose();
                    } else {
                        realPlayer._messageManager?.dispose?.();
                    }
                } catch (err) {
                    console.warn("[GvPlayerAdapter] cleanup failed:", err);
                }
            }
            realPlayer = instance;
            if (instance) {
                attachListeners(instance);
            }
        },

        /**
         * @param {string} id
         */
        loadMaterial(id) {
            materialId = id || "";
            if (realPlayer?.load && materialId) {
                realPlayer.load(materialId);
            }
        },

        unloadMaterial() {
            if (realPlayer?.unload) {
                realPlayer.unload();
            }
        },

        destroy() {
            detachListeners();
            if (realPlayer) {
                try {
                    realPlayer.unload?.();
                    if (typeof realPlayer.dispose === "function") {
                        realPlayer.dispose();
                    } else {
                        realPlayer._messageManager?.dispose?.();
                    }
                } catch (err) {
                    console.warn("[GvPlayerAdapter] dispose failed:", err);
                }
            }
            realPlayer = null;
            playing = false;
            currentTime = 0;
            duration = 0;
        },

        /** @returns {object|null} */
        getRealPlayer() {
            return realPlayer;
        },

        subscribe() {
            return () => {};
        },
    };

    return adapter;
}
